import {
  AfterViewInit,
  Component,
  ComponentRef,
  effect,
  inject,
  input,
  InputSignal,
  OnInit,
  PLATFORM_ID,
  signal,
  ViewChild,
  ViewContainerRef,
  WritableSignal
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { IHairdresser } from '../../../../shared/interfaces/hairdresser.interface';
import { GoogleMap, MapAdvancedMarker, MapInfoWindow, MapDirectionsRenderer } from '@angular/google-maps';
import { InfoWindowTitleComponent } from '../info-window-title/info-window-title.component';
import { HairdresserMapDetailsComponent } from '../hairdresser-map-details/hairdresser-map-details.component';
import { ToastrService } from 'ngx-toastr';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'braidsbook-map',
  imports: [GoogleMap, MapInfoWindow, HairdresserMapDetailsComponent, MapAdvancedMarker, MapDirectionsRenderer, TranslatePipe],
  templateUrl: './map.component.html'
})
export class MapComponent implements OnInit, AfterViewInit {
  readonly #viewContainer: ViewContainerRef = inject(ViewContainerRef);
  readonly #toastrService: ToastrService = inject(ToastrService);
  readonly #platformId = inject(PLATFORM_ID);

  @ViewChild('map') braidsbookMap!: GoogleMap;
  @ViewChild(MapInfoWindow, { static: false }) mapInfoWindow!: MapInfoWindow;
  infoWindowTitle: ComponentRef<InfoWindowTitleComponent> = this.#viewContainer.createComponent(InfoWindowTitleComponent);
  hairdressers: InputSignal<IHairdresser[]> = input.required();
  userPosition: WritableSignal<{ lat: number; lng: number } | null> = signal(null);

  selectedHairdresser: WritableSignal<IHairdresser | null> = signal<IHairdresser | null>(null);
  distance: WritableSignal<number | null> = signal<number | null>(null);
  directionsResult: WritableSignal<google.maps.DirectionsResult | null> = signal<google.maps.DirectionsResult | null>(null);
  isCalculatingRoute: WritableSignal<boolean> = signal(false);
  isRequestingLocation: WritableSignal<boolean> = signal(false);
  mapOptions: WritableSignal<google.maps.MapOptions> = signal<google.maps.MapOptions>({
    mapId: '9aa52ac6150fa220',
    // mapTypeId sera défini dans ngOnInit côté client car google.maps n'existe pas côté serveur
    mapTypeControl: false,
    fullscreenControl: false,
    zoomControl: false,
    streetViewControl: false,
    gestureHandling: 'greedy',
    center: { // canada
      lat: 56.130366,
      lng: -106.346771
    }
  });

  ngOnInit(): void {
    if (isPlatformBrowser(this.#platformId) && typeof google !== 'undefined' && google.maps) {
      this.mapOptions.update(options => ({
        ...options,
        mapTypeId: google.maps.MapTypeId.ROADMAP
      }));
    }
  }

  /**
   * Open the info window
   */
  openInfoWindow(mapMarker: MapAdvancedMarker, hairdresser: IHairdresser): void {
    this.selectedHairdresser.set(hairdresser);
    this.infoWindowTitle.setInput('title', this.selectedHairdresser()?.username);
    this.mapInfoWindow.infoWindow?.setOptions({
      headerContent: this.infoWindowTitle.location.nativeElement,
      minWidth: 200,
      maxWidth: 200
    });
    this.mapInfoWindow.open(mapMarker);

    // Calculer la distance et tracer la route si la position de l'utilisateur est disponible
    const userPos = this.userPosition();
    if (userPos && hairdresser.coordinates) {
      const distance = this.calculateDistance(
        userPos.lat,
        userPos.lng,
        hairdresser.coordinates.lat,
        hairdresser.coordinates.lng
      );
      this.distance.set(distance);
      // Réinitialiser la route précédente
      this.directionsResult.set(null);

      if (isPlatformBrowser(this.#platformId)) {
        this.calculateRoute(userPos, hairdresser.coordinates);
      }
    } else {
      this.distance.set(null);
      this.directionsResult.set(null);
      this.isCalculatingRoute.set(false);
    }
  }

  /**
   * Calcule la distance entre deux points en kilomètres (formule de Haversine)
   */
  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371; // Rayon de la Terre en km
    const dLat = this.toRad(lat2 - lat1);
    const dLon = this.toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Convertit les degrés en radians
   */
  private toRad(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  /**
   * Calcule et trace la route entre l'utilisateur et le hairdresser
   */
  private calculateRoute(origin: { lat: number; lng: number }, destination: { lat: number; lng: number }): void {
    if (!isPlatformBrowser(this.#platformId) || !this.braidsbookMap?.googleMap) {
      return;
    }

    if (typeof google === 'undefined') {
      return;
    }

    // Activer le loader
    this.isCalculatingRoute.set(true);

    const directionsService = new google.maps.DirectionsService();
    const request: google.maps.DirectionsRequest = {
      origin: new google.maps.LatLng(origin.lat, origin.lng),
      destination: new google.maps.LatLng(destination.lat, destination.lng),
      travelMode: google.maps.TravelMode.DRIVING
    };

    directionsService.route(request, (result, status) => {
      // Désactiver le loader
      this.isCalculatingRoute.set(false);

      if (status === google.maps.DirectionsStatus.OK && result) {
        this.directionsResult.set(result);
      } else {
        console.error('Erreur lors du calcul de la route:', status);
        this.directionsResult.set(null);
        // Afficher un toastr d'erreur
        this.#toastrService.error('Impossible de tracer la route. La distance reste disponible.', 'Erreur de route');
      }
    });
  }

  constructor() {
    // Demander la position de l'utilisateur uniquement côté client
    if (isPlatformBrowser(this.#platformId)) {
      this.requestUserLocation();
    }

    // Réagit aux changements de hairdressers et de la position de l'utilisateur pour mettre à jour les bounds
    effect(() => {
      const hairdressers = this.hairdressers();
      const userPos = this.userPosition();
      if ((hairdressers && hairdressers.length > 0) || userPos) {
        setTimeout(() => this.fitMapToMarkers(), 100);
      }
    });
  }

  /**
   * Demande la position de l'utilisateur
   */
  requestUserLocation(): void {
    if (navigator.geolocation) {
      // Activer l'état de chargement
      this.isRequestingLocation.set(true);

      // Première tentative avec options optimisées pour iOS
      // On essaie d'abord avec enableHighAccuracy: false pour être plus rapide
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newPosition = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          this.userPosition.set(newPosition);
          this.isRequestingLocation.set(false);

          // Ajuster la carte pour inclure la nouvelle position
          setTimeout(() => {
            this.fitMapToMarkers();
          }, 100);

          this.#toastrService.success('Position récupérée avec succès', 'Géolocalisation');
        },
        (error) => {
          // Si la première tentative échoue, essayer avec enableHighAccuracy: true
          // Cela peut aider sur iOS qui peut nécessiter plus de temps

          navigator.geolocation.getCurrentPosition(
            (position) => {
              const newPosition = {
                lat: position.coords.latitude,
                lng: position.coords.longitude
              };
              this.userPosition.set(newPosition);
              this.isRequestingLocation.set(false);

              // Ajuster la carte pour inclure la nouvelle position
              setTimeout(() => {
                this.fitMapToMarkers();
              }, 100);

              this.#toastrService.success('Position récupérée avec succès', 'Géolocalisation');
            },
            (secondError) => {
              // Désactiver l'état de chargement
              this.isRequestingLocation.set(false);

              // L'utilisateur a refusé ou une erreur s'est produite
              this.userPosition.set(null);

              // Log détaillé pour le débogage
              console.error('Erreur de géolocalisation:', {
                code: secondError.code,
                message: secondError.message,
                PERMISSION_DENIED: secondError.PERMISSION_DENIED,
                POSITION_UNAVAILABLE: secondError.POSITION_UNAVAILABLE,
                TIMEOUT: secondError.TIMEOUT
              });

              // Détecter si on est sur mobile ou desktop
              const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

              let errorMessage = 'Impossible d\'obtenir votre position.';
              if (secondError.code === secondError.PERMISSION_DENIED) {
                errorMessage = 'Accès à la géolocalisation refusé. Veuillez autoriser l\'accès dans les paramètres de votre navigateur.';
              } else if (secondError.code === secondError.POSITION_UNAVAILABLE) {
                if (isMobile) {
                  errorMessage = 'Position indisponible. Vérifiez que votre GPS est activé et que vous avez une bonne réception.';
                } else {
                  errorMessage = 'Position indisponible. Vérifiez que les services de localisation sont activés dans les paramètres de votre système et de votre navigateur.';
                }
              } else if (secondError.code === secondError.TIMEOUT) {
                errorMessage = 'La demande de position a expiré. Veuillez réessayer.';
              }

              this.#toastrService.error(errorMessage, 'Erreur de géolocalisation');
            },
            {
              enableHighAccuracy: true,
              timeout: 30000, // Augmenté à 30 secondes pour laisser plus de temps
              maximumAge: 300000 // Permettre d'utiliser une position mise en cache jusqu'à 5 minutes
            }
          );
        },
        {
          enableHighAccuracy: false, // Commencer avec false pour être plus rapide
          timeout: 15000, // 15 secondes pour la première tentative
          maximumAge: 60000 // Permettre d'utiliser une position mise en cache jusqu'à 1 minute
        }
      );
    } else {
      this.#toastrService.error('La géolocalisation n\'est pas supportée par votre navigateur.', 'Erreur');
    }
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.#platformId)) {
      return;
    }

    if (this.braidsbookMap?.googleMap) {
      this.fitMapToMarkers();
    } else {
      setTimeout(() => this.fitMapToMarkers(), 200);
    }
  }

  private fitMapToMarkers(): void {
    if (!isPlatformBrowser(this.#platformId) || !this.braidsbookMap?.googleMap || typeof google === 'undefined') {
      return;
    }

    const userPos = this.userPosition();
    const hairdressers = this.hairdressers() || [];
    const bounds = new google.maps.LatLngBounds();
    let hasValidCoordinates = false;

    // Si l'utilisateur a donné sa position, l'ajouter aux bounds
    if (userPos) {
      bounds.extend(
        new google.maps.LatLng(
          userPos.lat,
          userPos.lng
        )
      );
    }

    // Ajouter tous les hairdressers aux bounds
    hairdressers.forEach(hairdresser => {
      if (hairdresser.coordinates &&
        typeof hairdresser.coordinates.lat === 'number' &&
        typeof hairdresser.coordinates.lng === 'number') {
        bounds.extend(
          new google.maps.LatLng(
            hairdresser.coordinates.lat,
            hairdresser.coordinates.lng
          )
        );
        hasValidCoordinates = true;
      }
    });

    if (hasValidCoordinates || userPos) {
      // Si on a la position de l'utilisateur et des hairdressers, utiliser fitBounds avec padding
      if (userPos && hasValidCoordinates) {
        this.braidsbookMap.fitBounds(bounds, 50);
      } else if (userPos) {
        // Si seulement la position de l'utilisateur, centrer sur lui
        this.braidsbookMap.googleMap.setCenter({
          lat: userPos.lat,
          lng: userPos.lng
        });
        this.braidsbookMap.googleMap.setZoom(15);
      } else if (hasValidCoordinates) {
        // Sinon, juste fitBounds sur les hairdressers
        this.braidsbookMap.fitBounds(bounds);
      }
    }
  }

}
